import { Select } from 'antd';
import {
  ROTATION_ENTERPRISE_EMPTY_MESSAGE,
  ROTATION_SEED_EMPTY_MESSAGE,
  ROTATION_SEED_HELP,
  ROTATION_SEED_MISSING_MESSAGE,
} from 'constants/messageConstants';
import {
  ROTATION_AREA_HA,
  ROTATION_ENTERPRISE_TITLE,
  ROTATION_ENTERPRISE_TOTAL,
  ROTATION_ENTERPRISE_VALUE,
  ROTATION_MISSING_PLAN_LINE,
  ROTATION_MORE_COUNT,
  ROTATION_SEED_AREA_COLUMN,
  ROTATION_SEED_CROP_COLUMN,
  ROTATION_SEED_KG,
  ROTATION_SEED_QUANTITY_COLUMN,
  ROTATION_SEED_T,
  ROTATION_SEED_TITLE,
  ROTATION_SUMMARY_SUBTITLE,
  ROTATION_SUMMARY_YEAR_LABEL,
  SUMMARY_DATA_BASIS_ACTUAL,
  SUMMARY_DATA_BASIS_LABELS,
  SUMMARY_DATA_BASIS_PLAN,
} from 'constants/uiConstants';
import type { GUnit } from 'interfaces/IGlobalCatalogue';
import type { IInput } from 'interfaces/IInput';
import { InputTaskCategory } from 'interfaces/IInputTask';
import React, { useMemo } from 'react';
import { LuAlertTriangle, LuInfo } from 'react-icons/lu';
import ChartGhostState from 'pages/FarmsPage/Workspace/FarmDiagnostics/ChartGhostState';
import IconTip from 'pages/FarmsPage/Workspace/Insights/nutrition/IconTip';
import { formatAreaHa } from 'pages/FarmsPage/Workspace/Insights/nutrition/nutritionFormat';
import SectionCard from 'pages/FarmsPage/Workspace/Insights/summary2/SectionCard';
import type { RotationGrid } from './computeRotationGrid';
import { computeRotationSummary } from './computeRotationSummary';
import { cropMarkClass } from './cropColour';

/** Missing plans listed by name; the rest are counted. */
const MISSING_LIST_MAX = 5;
/** Placeholder bars and rows, dimmed under the empty-state message. */
const GHOST_ROWS = ['a', 'b', 'c'];
const GHOST_SHARES = [0.55, 0.3, 0.15];

const percentFormat = new Intl.NumberFormat(undefined, {
  maximumFractionDigits: 0,
});
const tonnesFormat = new Intl.NumberFormat(undefined, {
  maximumFractionDigits: 1,
});
const kgFormat = new Intl.NumberFormat(undefined, {
  maximumFractionDigits: 0,
});

/** Seed in kg below a tonne, otherwise tonnes. */
const formatSeed = (kg: number) =>
  kg < 1000
    ? ROTATION_SEED_KG(kgFormat.format(kg))
    : ROTATION_SEED_T(tonnesFormat.format(kg / 1000));

/** A horizontal bar for a share of the total, drawn with SVG attributes. */
const ShareBar: React.FC<{ share: number; className: string }> = ({
  share,
  className,
}) => (
  <svg
    viewBox="0 0 100 6"
    preserveAspectRatio="none"
    className="h-2 w-full"
    aria-hidden
  >
    <rect width="100" height="6" rx="1" className="fill-secondary-light" />
    <rect
      width={Math.max(share * 100, 1)}
      height="6"
      rx="1"
      className={className}
    />
  </svg>
);

interface RotationSummaryPanelProps {
  grid: RotationGrid;
  seasons: number[];
  season: number;
  onSeasonChange: (season: number) => void;
  planningSeason: number;
  pastCategory: InputTaskCategory;
  inputs: IInput[];
  gUnits: GUnit[];
}

/**
 * The Rotation planner's summary (OP-2501): the enterprise mix and the seed
 * needed for one year, from the plans the grid shows — so it follows the
 * grid's scope and Plan / Actual choice and updates as soon as a cell is
 * saved. Plans with no sowing rate are listed, so the seed total is never
 * quietly short.
 */
const RotationSummaryPanel: React.FC<RotationSummaryPanelProps> = ({
  grid,
  seasons,
  season,
  onSeasonChange,
  planningSeason,
  pastCategory,
  inputs,
  gUnits,
}) => {
  const inputsById = useMemo(
    () => new Map(inputs.map((input) => [input.id, input])),
    [inputs]
  );
  const summary = useMemo(
    () => computeRotationSummary(grid, season, inputsById, gUnits),
    [grid, season, inputsById, gUnits]
  );
  const basis =
    season < planningSeason && pastCategory === InputTaskCategory.Actual
      ? SUMMARY_DATA_BASIS_LABELS[SUMMARY_DATA_BASIS_ACTUAL]
      : SUMMARY_DATA_BASIS_LABELS[SUMMARY_DATA_BASIS_PLAN];
  const subtitle = ROTATION_SUMMARY_SUBTITLE(season, basis);

  const { enterprise, seed } = summary;
  const enterpriseEmpty = enterprise.slices.length === 0;
  const seedEmpty = seed.lines.length === 0;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-2">
        <label className="flex items-center gap-2 text-sm font-semibold text-gray-dark">
          {ROTATION_SUMMARY_YEAR_LABEL}
          <Select<number>
            aria-label={ROTATION_SUMMARY_YEAR_LABEL}
            className="w-24"
            value={season}
            options={seasons.map((value) => ({ value, label: String(value) }))}
            onChange={onSeasonChange}
          />
        </label>
      </div>

      <SectionCard
        id="rotation-enterprise-mix"
        variant="result"
        title={ROTATION_ENTERPRISE_TITLE}
        subtitle={subtitle}
      >
        <ChartGhostState
          active={enterpriseEmpty}
          message={ROTATION_ENTERPRISE_EMPTY_MESSAGE}
        >
          <ul className="flex flex-col gap-2">
            {(enterpriseEmpty
              ? GHOST_ROWS.map((key, i) => ({
                  cropName: key,
                  areaHa: 0,
                  share: GHOST_SHARES[i],
                }))
              : enterprise.slices
            ).map((slice) => (
              <li key={slice.cropName} className="flex flex-col gap-0.5">
                <span className="flex items-baseline justify-between gap-2 text-sm">
                  <span className="truncate font-semibold text-charcoal">
                    {enterpriseEmpty ? '' : slice.cropName}
                  </span>
                  <span className="flex-shrink-0 text-gray-dark">
                    {enterpriseEmpty
                      ? ''
                      : ROTATION_ENTERPRISE_VALUE(
                          formatAreaHa(slice.areaHa),
                          percentFormat.format(slice.share * 100)
                        )}
                  </span>
                </span>
                <ShareBar
                  share={slice.share}
                  className={
                    enterpriseEmpty
                      ? 'fill-gray-medium'
                      : cropMarkClass(slice.cropName)
                  }
                />
              </li>
            ))}
          </ul>
          {!enterpriseEmpty && (
            <p className="mt-2 text-sm text-gray-dark">
              {ROTATION_ENTERPRISE_TOTAL(formatAreaHa(enterprise.totalHa))}
            </p>
          )}
        </ChartGhostState>
      </SectionCard>

      <SectionCard
        id="rotation-seed-requirements"
        variant="result"
        title={ROTATION_SEED_TITLE}
        subtitle={subtitle}
        actions={
          <IconTip title={ROTATION_SEED_HELP}>
            <LuInfo size={16} aria-hidden />
          </IconTip>
        }
      >
        <ChartGhostState active={seedEmpty} message={ROTATION_SEED_EMPTY_MESSAGE}>
          <div className="plan-preview-container">
            <table className="stack-table-narrow w-full border-collapse text-left text-sm">
              <thead>
                <tr className="border-b border-gray-medium text-gray-dark">
                  <th scope="col" className="py-1 pr-2 font-semibold">
                    {ROTATION_SEED_CROP_COLUMN}
                  </th>
                  <th scope="col" className="py-1 pr-2 text-right font-semibold">
                    {ROTATION_SEED_AREA_COLUMN}
                  </th>
                  <th scope="col" className="py-1 text-right font-semibold">
                    {ROTATION_SEED_QUANTITY_COLUMN}
                  </th>
                </tr>
              </thead>
              <tbody>
                {(seedEmpty
                  ? GHOST_ROWS.map((key) => ({
                      key,
                      cropName: '—',
                      varietyName: null,
                      areaHa: null,
                      quantityKg: null,
                    }))
                  : seed.lines
                ).map((line) => (
                  <tr key={line.key} className="border-b border-gray-medium">
                    <td className="stack-primary py-1 pr-2 text-charcoal">
                      <span className="font-semibold">{line.cropName}</span>
                      {line.varietyName && (
                        <span className="text-gray-dark"> · {line.varietyName}</span>
                      )}
                    </td>
                    <td
                      data-label={ROTATION_SEED_AREA_COLUMN}
                      className="py-1 pr-2 text-right text-gray-dark"
                    >
                      {line.areaHa == null
                        ? '—'
                        : ROTATION_AREA_HA(formatAreaHa(line.areaHa))}
                    </td>
                    <td
                      data-label={ROTATION_SEED_QUANTITY_COLUMN}
                      className="py-1 text-right font-semibold text-charcoal"
                    >
                      {line.quantityKg == null ? '—' : formatSeed(line.quantityKg)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </ChartGhostState>
        {seed.missing.length > 0 && (
          <div className="mt-3 flex gap-2 text-sm text-charcoal">
            <LuAlertTriangle
              size={16}
              aria-hidden
              className="mt-0.5 flex-shrink-0 text-danger-dark"
            />
            <div>
              <p>
                {ROTATION_SEED_MISSING_MESSAGE(
                  seed.missing.length,
                  formatAreaHa(seed.missingHa)
                )}
              </p>
              <ul className="text-gray-dark">
                {seed.missing.slice(0, MISSING_LIST_MAX).map((plan) => (
                  <li key={plan.inputId}>
                    {ROTATION_MISSING_PLAN_LINE(plan.paddockName, plan.cropName)}
                  </li>
                ))}
                {seed.missing.length > MISSING_LIST_MAX && (
                  <li>
                    {ROTATION_MORE_COUNT(seed.missing.length - MISSING_LIST_MAX)}
                  </li>
                )}
              </ul>
            </div>
          </div>
        )}
      </SectionCard>
    </div>
  );
};

export default RotationSummaryPanel;
